/**
 * Flight Budget Reconciliation
 *
 * The invariant "flight budgets sum to the line item's budgetTotal" holds at
 * creation time (blueprint apply, Copy from Campaign, CSV import) but nothing
 * maintained it afterwards: every budget editor rewrote budgetTotal without
 * touching flights, silently corrupting delivery data.
 *
 * rescaleFlightsToBudget is the reconcile step those editors now call: it
 * redistributes the new budget across the existing flights, preserving each
 * flight's current budget proportion (its phasing) and dealing rounding in
 * integer cents so the flights sum exactly to the new budget. Flights with
 * no budget split evenly.
 */

import { distributeCentsProRata } from './renewalFlights';

/**
 * Rescale flight budgets so they sum exactly to `newBudget`.
 *
 * @param {Array} flights – existing flight schedule entries
 * @param {number} newBudget – the line item's new budgetTotal
 * @returns {Array} new flights array with rescaled budgets, or the original
 *          array when there is nothing to do
 */
export const rescaleFlightsToBudget = (flights, newBudget) => {
  if (!Array.isArray(flights) || flights.length === 0) return flights;

  const target = Number(newBudget);
  if (!Number.isFinite(target) || target < 0) return flights;

  const currentTotal = flights.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
  if (Math.abs(currentTotal - target) < 0.005) return flights;

  const weights = flights.map((f) => Number(f.budget) || 0);
  const shares = distributeCentsProRata(Math.round(target * 100), weights);

  return flights.map((flight, i) => ({
    ...flight,
    budget: shares[i] / 100,
  }));
};

/**
 * Rescale whichever flight field exists on a line item (the codebase uses
 * both `flightSchedule` and `flightSchedules`), returning a new line item.
 * No-op when the line item has no flights.
 *
 * @param {Object} lineItem
 * @param {number} newBudget
 * @returns {Object} new line item object (same reference when unchanged)
 */
export const rescaleLineItemFlightsToBudget = (lineItem, newBudget) => {
  if (!lineItem) return lineItem;
  const field = Array.isArray(lineItem.flightSchedule)
    ? 'flightSchedule'
    : Array.isArray(lineItem.flightSchedules)
      ? 'flightSchedules'
      : null;
  if (!field) return lineItem;

  const rescaled = rescaleFlightsToBudget(lineItem[field], newBudget);
  if (rescaled === lineItem[field]) return lineItem;
  return { ...lineItem, [field]: rescaled };
};

/**
 * Deal a campaign's unallocated budget gap (campaign.budget − Σ delivery line
 * item budgetTotal) across its DELIVERY line items so the items sum exactly to
 * the campaign budget. Works in integer cents: the whole campaign budget is
 * re-dealt pro-rata by the items' current budgets (largest-remainder), which
 * preserves each item's share to the cent and guarantees an exact sum.
 * Reference (non-delivery) items are untouched. Each modified item's flights
 * are rescaled to its new budget.
 *
 * @param {Object} campaign – form-state campaign (budget, lineItems)
 * @returns {Object} new campaign object (same reference when nothing to do)
 */
export const distributeCampaignGapToLineItems = (campaign) => {
  if (!campaign) return campaign;
  const budgetCents = Math.round((Number(campaign.budget) || 0) * 100);
  const lineItems = Array.isArray(campaign.lineItems) ? campaign.lineItems : [];

  const deliverableIdx = lineItems
    .map((lineItem, idx) => (lineItem.markedForDelivery !== false ? idx : -1))
    .filter((idx) => idx !== -1);

  if (deliverableIdx.length === 0) return campaign;

  const weights = deliverableIdx.map((idx) => Number(lineItems[idx].budgetTotal) || 0);
  const currentTotalCents = weights.reduce((sum, w) => sum + Math.round(w * 100), 0);
  if (currentTotalCents === budgetCents) return campaign; // no gap

  const shares = distributeCentsProRata(budgetCents, weights);

  const newLineItems = lineItems.map((lineItem, idx) => {
    const pos = deliverableIdx.indexOf(idx);
    if (pos === -1) return lineItem;

    const newBudget = shares[pos] / 100;
    const budget = budgetCents / 100;
    return rescaleLineItemFlightsToBudget(
      {
        ...lineItem,
        budgetTotal: newBudget,
        budgetPercentage: budget > 0 ? parseFloat(((newBudget / budget) * 100).toFixed(1)) : 0,
      },
      newBudget
    );
  });

  return { ...campaign, lineItems: newLineItems };
};
